import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Star,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Sparkles,
  FileText,
  Users,
  MessageSquare,
  BarChart3,
  Shield,
  Target,
  Zap,
  TrendingUp,
  LineChart,
  Brain,
  Scale,
  Cpu,
  Megaphone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { PHEvents } from '@/lib/posthog';
import { FloatingBriefPrompt } from '@/components/landing/FloatingBriefPrompt';
import { useLandingPromptPhrases } from '@/hooks/useLandingPromptPhrases';
import { useTypingSuggestions } from '@/hooks/useTypingSuggestions';
import { addBriefSeoContextParam } from '@/lib/briefSeoContext';
import { detectGoogleAdsKeyword } from '@/lib/googleAdsKeyword';
import { writeAIChatHiddenContext } from '@/lib/aichatHiddenContext';

const AnimatedConsultantCard = lazy(() =>
  import('@/components/AnimatedConsultantCard').then((m) => ({ default: m.AnimatedConsultantCard }))
);

const DISPLAY_FONT = '"Aptos", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const AICHAT_LANDING_MESSAGE_KEY = 'aichatLandingInitialMessage';
const AICHAT_FORCE_NEW_KEY = 'aichatForceNew';

type LandingVariant = 'main' | 'e2' | 'eFinanzaAgevolata2' | 'lCom' | 'lComB' | 'lSec';

type HowItWorksItem = {
  step: string;
  title: string;
  desc: string;
  icon: LucideIcon;
};

type CustomerFeatureItem = {
  title: string;
  desc: string;
  icon: LucideIcon;
};

type LandingCopy = {
  landingKey: string;
  hiddenContextSource: string;
  seo: {
    title: string;
    description: string;
    canonical: string;
    noindex?: boolean;
  };
  hero: {
    pageTitle: string;
    pageSubtitle: string;
    pageCtaHint: string;
    headlinePrefix: string;
    headlineHighlight: string;
    headlineConnector?: string;
    headlineSecondHighlight?: string;
    headlineSuffix: string;
    intro: string;
    boxHeadingMobile: string;
    boxHeadingDesktopLine1: string;
    boxHeadingDesktopLine2: string;
    ctaLabel: string;
  };
  howItWorks: {
    title: string;
    intro: string;
    items: HowItWorksItem[];
  };
  trust: {
    title: string;
    intro: string;
    ctaLabel: string;
    items: CustomerFeatureItem[];
  };
};

const landingCopy: Record<LandingVariant, LandingCopy> = {
  main: {
    landingKey: 'landing-main',
    hiddenContextSource: 'main_landing',
    seo: {
      title: 'Trova la Società di Consulenza più Adatta alla Tua Azienda | Dealin',
      description:
        'Piattaforma AI per trovare la società di consulenza ideale. Confronta consulenti IT, marketing, fiscale e compliance con recensioni verificate. Assessment gratuito.',
      canonical: 'https://www.deal-in.it/',
    },
    hero: {
      pageTitle: 'Troviamo per te il Consulente Perfetto in linea con le tue esigenze',
      pageSubtitle: 'Accedi a società di consulenza verificate e trova il consulente più adatto al tuo progetto aziendale.',
      pageCtaHint: 'Descrivi la tua richiesta e ottieni un primo assessment strutturato in pochi minuti.',
      headlinePrefix: 'Troviamo per te il ',
      headlineHighlight: 'Consulente Perfetto',
      headlineSuffix: ' in linea con le tue esigenze.',
      intro:
        'Accedi a società di consulenza verificate, recensioni attendibili e dati utili per orientare la tua scelta in modo semplice e trasparente.',
      boxHeadingMobile: 'Descrivi il tuo progetto e le tue esigenze',
      boxHeadingDesktopLine1: 'Descrivi il tuo progetto e le tue esigenze.',
      boxHeadingDesktopLine2: 'L\'AI interpreta il contesto e ti guida verso i consulenti giusti.',
      ctaLabel: 'Avvia l\'analisi AI',
    },
    howItWorks: {
      title: 'Dealin: diverse esigenze, un unica soluzione',
      intro:
        'Dealin è l\'unica piattaforma che ti accompagna dall\'inizio alla fine, con un percorso guidato e completamente gratuito, per la ricerca e la selezione del consulente giusto per lo sviluppo del tuo progetto.',
      items: [
        {
          step: '01',
          title: 'Definisci i requisiti',
          desc: 'Descrivi ciò che ti serve e l\'AI ti guiderà con domande e sezioni inerenti per un primo assessment del progetto',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Analizza i risultati',
          desc: 'Una volta completato l\'assessment, l\'algoritmo di Dealin ti proporrà una lista di consulenti adatti alle tue specifiche',
          icon: Users,
        },
        {
          step: '03',
          title: 'Avvia la collaborazione',
          desc: 'Trovato il consulente adatto a te, inizia la comunicazione e invia il tuo assessment per ricevere una risposta mirata',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Requisiti, dati e recensioni verificate alla base delle tue attività di Scouting',
      intro:
        'Con Dealin potrai superare le barriere informative per la scelta del consulente giusto, attraverso un processo semplice, intuitivo e basato su informazioni reali e verificate',
      ctaLabel: 'Cerca consulenti',
      items: [
        {
          title: 'Verifica dell\'identità',
          desc: 'Il processo di verifica dell\'identità aziendale è un requisito estremamente importante per Dealin e per garantire la massima qualità delle offerte.',
          icon: Shield,
        },
        {
          title: 'Recensioni Attendibili',
          desc: 'Attraverso un sistema di doppia verifica, Dealin garantisce che tutte le recensioni siano certificate dall\'effettiva erogazione di un servizio.',
          icon: Star,
        },
        {
          title: 'Certificazioni',
          desc: 'Dealin garantisce la veridicità delle informazioni riguardanti le certificazioni, garantendo affidabilità e trasparenza.',
          icon: Scale,
        },
      ],
    },
  },
  e2: {
    landingKey: 'landing-e2',
    hiddenContextSource: 'main_landing_e2',
    seo: {
      title: 'Trova consulenti qualificati e adatti al tuo progetto | Dealin',
      description:
        'Ti guidiamo nella scelta dei professionisti più rilevanti per il tuo progetto, con un processo più semplice, veloce e mirato.',
      canonical: 'https://www.deal-in.it/e2',
      noindex: true,
    },
    hero: {
      pageTitle: 'Trova consulenti qualificati e adatti al tuo progetto',
      pageSubtitle:
        'Ti guidiamo nella scelta dei professionisti più rilevanti per il tuo progetto, con un processo più semplice, veloce e mirato.',
      pageCtaHint: 'Parti dal tuo obiettivo e ottieni una selezione di consulenti coerente e mirata in pochi minuti.',
      headlinePrefix: 'Trova ',
      headlineHighlight: 'consulenti qualificati',
      headlineSuffix: ' e adatti al tuo progetto',
      intro:
        'Ti guidiamo nella scelta dei professionisti più rilevanti per il tuo progetto, con un processo più semplice, veloce e mirato.',
      boxHeadingMobile: 'Parti dal tuo obiettivo: Dealin trasforma la tua esigenza in una selezione di consulenti coerente e mirata.',
      boxHeadingDesktopLine1: 'Parti dal tuo obiettivo: Dealin trasforma la tua esigenza',
      boxHeadingDesktopLine2: 'in una selezione di consulenti coerente e mirata.',
      ctaLabel: 'Inizia gratuitamente',
    },
    howItWorks: {
      title: 'Cosa cambia con Dealin',
      intro:
        'Con Dealin la ricerca del consulente diventa più chiara, mirata e strutturata: meno dispersione, più coerenza con il tuo progetto e più supporto nella fase di scelta.',
      items: [
        {
          step: '01',
          title: 'Meno ricerca dispersiva',
          desc: 'Non parti da elenchi generici o contatti raccolti in modo informale: inizi dal tuo bisogno e da ciò che serve davvero al progetto.',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Più coerenza nella selezione',
          desc: 'Dealin interpreta il contesto della tua richiesta e ti orienta verso consulenti più rilevanti per ambito, obiettivi e tipo di supporto.',
          icon: Users,
        },
        {
          step: '03',
          title: 'Più chiarezza nella scelta',
          desc: 'Valuti i profili in modo più strutturato e arrivi alla collaborazione con un processo più semplice, ordinato e consapevole.',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Più fiducia nella scelta del consulente',
      intro:
        'Con Dealin hai a disposizione informazioni più strutturate e verifiche utili per valutare i consulenti con maggiore consapevolezza, riducendo incertezza e dispersione nella fase di scouting.',
      ctaLabel: 'Trova consulenti',
      items: [
        {
          title: 'Profili verificati',
          desc: 'Le informazioni chiave sui consulenti vengono raccolte e organizzate per offrirti una base più solida da cui partire nella valutazione.',
          icon: Shield,
        },
        {
          title: 'Recensioni verificate',
          desc: 'Le recensioni sono raccolte attraverso un processo di verifica, per aiutarti a leggere l’esperienza dei clienti in modo più affidabile e utile alla scelta.',
          icon: Star,
        },
        {
          title: 'Informazioni più affidabili',
          desc: 'Dati, competenze e segnali utili alla valutazione sono presentati in modo più trasparente, per rendere il confronto tra consulenti più semplice e consapevole.',
          icon: Scale,
        },
      ],
    },
  },
  eFinanzaAgevolata2: {
    landingKey: 'landing-e-finanza-agevolata-2',
    hiddenContextSource: 'main_landing_e_finanza_agevolata_2',
    seo: {
      title: 'Trova consulenti in finanza agevolata adatti al tuo progetto | Dealin',
      description:
        'Ti guidiamo nella scelta di professionisti più rilevanti per il tuo progetto, con un percorso più semplice, veloce e mirato.',
      canonical: 'https://www.deal-in.it/e-finanza-agevolata-2',
      noindex: true,
    },
    hero: {
      pageTitle: 'Trova consulenti in finanza agevolata adatti al tuo progetto',
      pageSubtitle:
        'Ti guidiamo nella scelta di professionisti più rilevanti per il tuo progetto, con un percorso più semplice, veloce e mirato.',
      pageCtaHint: 'Parti dal tuo obiettivo e ottieni una selezione di consulenti coerente e mirata in pochi minuti.',
      headlinePrefix: 'Trova consulenti in ',
      headlineHighlight: 'finanza agevolata',
      headlineSuffix: ' adatti al tuo progetto',
      intro:
        'Ti guidiamo nella scelta di professionisti più rilevanti per il tuo progetto, con un percorso più semplice, veloce e mirato.',
      boxHeadingMobile: 'Parti dal tuo obiettivo: Dealin trasforma la tua esigenza in una selezione di consulenti coerente e mirata.',
      boxHeadingDesktopLine1: 'Parti dal tuo obiettivo: Dealin trasforma la tua esigenza',
      boxHeadingDesktopLine2: 'in una selezione di consulenti coerente e mirata.',
      ctaLabel: 'Inizia gratuitamente',
    },
    howItWorks: {
      title: 'Perché usare Dealin',
      intro:
        'Con Dealin la ricerca del consulente in finanza agevolata diventa più chiara, mirata e strutturata: meno dispersione, più coerenza con il tuo progetto e più supporto nella fase di scelta.',
      items: [
        {
          step: '01',
          title: 'Parti dal tuo bisogno reale',
          desc: 'Non parti da elenchi generici o contatti raccolti in modo informale: inizi dal tuo bisogno e da ciò che serve davvero al progetto.',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Consulenti più in linea con il tuo caso',
          desc: 'Dealin interpreta il contesto della tua richiesta e ti orienta verso consulenti più rilevanti per ambito, obiettivi e tipo di supporto.',
          icon: Users,
        },
        {
          step: '03',
          title: 'Più sicurezza nella valutazione',
          desc: 'Confronti i profili con maggiore chiarezza e puoi scegliere con più consapevolezza da chi partire.',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Informazioni più affidabili per scegliere meglio',
      intro:
        'Con Dealin hai a disposizione informazioni più strutturate e verifiche utili per valutare i consulenti con maggiore consapevolezza, riducendo incertezza e dispersione nella fase di scouting.',
      ctaLabel: 'Trova consulenti',
      items: [
        {
          title: 'Profili verificati',
          desc: 'Le informazioni chiave sui consulenti vengono raccolte e organizzate per offrirti una base più solida da cui partire nella valutazione.',
          icon: Shield,
        },
        {
          title: 'Recensioni verificate',
          desc: 'Le recensioni sono raccolte attraverso un processo di verifica, per aiutarti a leggere l’esperienza dei clienti in modo più affidabile e utile alla scelta.',
          icon: Star,
        },
        {
          title: 'Più chiarezza nel confronto',
          desc: 'Dati, competenze e segnali utili alla valutazione sono presentati in modo più trasparente, per rendere il confronto tra consulenti più semplice e consapevole.',
          icon: Scale,
        },
      ],
    },
  },
  lCom: {
    landingKey: 'landing-l-com',
    hiddenContextSource: 'main_landing_l_com',
    seo: {
      title: 'Trova il commercialista più adatto alla tua attività | Dealin',
      description:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      canonical: 'https://www.deal-in.it/l-com-a',
      noindex: true,
    },
    hero: {
      pageTitle: 'Trova il commercialista più adatto alla tua attività',
      pageSubtitle:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      pageCtaHint: 'Parti dal tuo caso e ottieni una selezione di commercialisti coerente e mirata in pochi minuti.',
      headlinePrefix: 'Trova il ',
      headlineHighlight: 'commercialista',
      headlineSuffix: ' più adatto alla tua attività',
      intro:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      boxHeadingMobile:
        'Parti dal tuo caso: Dealin trasforma la tua esigenza in una selezione di commercialisti coerente e mirata.',
      boxHeadingDesktopLine1: 'Parti dal tuo caso: Dealin trasforma la tua esigenza',
      boxHeadingDesktopLine2: 'in una selezione di commercialisti coerente e mirata.',
      ctaLabel: 'Inizia gratuitamente',
    },
    howItWorks: {
      title: 'Più chiarezza nella scelta del commercialista',
      intro:
        'Con Dealin la ricerca del commercialista diventa più chiara, mirata e strutturata: meno dispersione, più coerenza con la tua attività e più supporto nella fase di scelta.',
      items: [
        {
          step: '01',
          title: 'Parti dal tuo bisogno reale',
          desc: 'Non parti da elenchi generici o contatti raccolti in modo informale: inizi da ciò che serve davvero alla tua azienda.',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Professionisti più in linea con il tuo caso',
          desc: 'Dealin interpreta il contesto della tua richiesta e ti orienta verso commercialisti più rilevanti per ambito, esigenze e tipo di supporto.',
          icon: Users,
        },
        {
          step: '03',
          title: 'Più sicurezza nella valutazione',
          desc: 'Confronti i profili con maggiore chiarezza e puoi scegliere con più consapevolezza da chi partire.',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Informazioni più affidabili per scegliere meglio',
      intro:
        'Con Dealin hai a disposizione informazioni più strutturate e verifiche utili per valutare i professionisti con maggiore consapevolezza, riducendo incertezza e dispersione nella fase di scouting.',
      ctaLabel: 'Trova commercialisti',
      items: [
        {
          title: 'Profili verificati',
          desc: 'Le informazioni chiave sui professionisti vengono raccolte e organizzate per offrirti una base più solida da cui partire nella valutazione.',
          icon: Shield,
        },
        {
          title: 'Recensioni verificate',
          desc: 'Le recensioni sono raccolte attraverso un processo di verifica, per aiutarti a leggere l’esperienza dei clienti in modo più affidabile e utile alla scelta.',
          icon: Star,
        },
        {
          title: 'Più chiarezza nel confronto',
          desc: 'Dati, competenze e segnali utili alla valutazione sono presentati in modo più trasparente, per rendere il confronto tra professionisti più semplice e consapevole.',
          icon: Scale,
        },
      ],
    },
  },
  lComB: {
    landingKey: 'landing-l-com-b',
    hiddenContextSource: 'main_landing_l_com_b',
    seo: {
      title: 'Trova il commercialista più adatto alla tua attività | Dealin',
      description:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      canonical: 'https://www.deal-in.it/l-com-b',
      noindex: true,
    },
    hero: {
      pageTitle: 'Trova il commercialista più adatto alla tua attività',
      pageSubtitle:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      pageCtaHint: 'Parti dal tuo caso e ottieni una selezione di commercialisti coerente e mirata in pochi minuti.',
      headlinePrefix: 'Trova il ',
      headlineHighlight: 'commercialista',
      headlineSuffix: ' più adatto alla tua attività',
      intro:
        'Ti guidiamo nella scelta di professionisti più rilevanti per la tua azienda, con un percorso più semplice, chiaro e mirato.',
      boxHeadingMobile:
        'Parti dal tuo caso: Dealin trasforma la tua esigenza in una selezione di commercialisti coerente e mirata.',
      boxHeadingDesktopLine1: 'Parti dal tuo caso: Dealin trasforma la tua esigenza',
      boxHeadingDesktopLine2: 'in una selezione di commercialisti coerente e mirata.',
      ctaLabel: 'Inizia gratuitamente',
    },
    howItWorks: {
      title: 'Più chiarezza nella scelta del commercialista',
      intro:
        'Con Dealin la ricerca del commercialista diventa più chiara, mirata e strutturata: meno dispersione, più coerenza con la tua attività e più supporto nella fase di scelta.',
      items: [
        {
          step: '01',
          title: 'Parti dal tuo bisogno reale',
          desc: 'Non parti da elenchi generici o contatti raccolti in modo informale: inizi da ciò che serve davvero alla tua azienda.',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Professionisti più in linea con il tuo caso',
          desc: 'Dealin interpreta il contesto della tua richiesta e ti orienta verso commercialisti più rilevanti per ambito, esigenze e tipo di supporto.',
          icon: Users,
        },
        {
          step: '03',
          title: 'Più sicurezza nella valutazione',
          desc: 'Confronti i profili con maggiore chiarezza e puoi scegliere con più consapevolezza da chi partire.',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Informazioni più affidabili per scegliere meglio',
      intro:
        'Con Dealin hai a disposizione informazioni più strutturate e verifiche utili per valutare i professionisti con maggiore consapevolezza, riducendo incertezza e dispersione nella fase di scouting.',
      ctaLabel: 'Trova commercialisti',
      items: [
        {
          title: 'Profili verificati',
          desc: 'Le informazioni chiave sui professionisti vengono raccolte e organizzate per offrirti una base più solida da cui partire nella valutazione.',
          icon: Shield,
        },
        {
          title: 'Recensioni verificate',
          desc: 'Le recensioni sono raccolte attraverso un processo di verifica, per aiutarti a leggere l’esperienza dei clienti in modo più affidabile e utile alla scelta.',
          icon: Star,
        },
        {
          title: 'Più chiarezza nel confronto',
          desc: 'Dati, competenze e segnali utili alla valutazione sono presentati in modo più trasparente, per rendere il confronto tra professionisti più semplice e consapevole.',
          icon: Scale,
        },
      ],
    },
  },
  lSec: {
    landingKey: 'landing-l-sec',
    hiddenContextSource: 'main_landing_l_sec',
    seo: {
      title: 'Trova il consulente più adatto su compliance e cyber security | Dealin',
      description:
        'Dealin ti aiuta a trovare professionisti più adatti al tuo contesto aziendale, per affrontare obblighi normativi, protezione dei dati e temi di sicurezza in modo più ordinato e consapevole.',
      canonical: 'https://www.deal-in.it/l-sec',
      noindex: true,
    },
    hero: {
      pageTitle: 'Trova il consulente più adatto su compliance e cyber security',
      pageSubtitle:
        'Dealin ti aiuta a trovare professionisti più adatti al tuo contesto aziendale, per affrontare obblighi normativi, protezione dei dati e temi di sicurezza in modo più ordinato e consapevole.',
      pageCtaHint:
        'Descrivi la tua esigenza e individua consulenti più adatti per compliance, protezione dei dati e sicurezza aziendale.',
      headlinePrefix: 'Trova il consulente più adatto su ',
      headlineHighlight: 'Compliance',
      headlineConnector: ' e ',
      headlineSecondHighlight: 'Cyber Security',
      headlineSuffix: '',
      intro:
        'Dealin ti aiuta a trovare professionisti più adatti al tuo contesto aziendale, per affrontare obblighi normativi, protezione dei dati e temi di sicurezza in modo più ordinato e consapevole.',
      boxHeadingMobile:
        'Descrivi la tua esigenza: Dealin ti aiuta a individuare consulenti più adatti per compliance, protezione dei dati e sicurezza aziendale.',
      boxHeadingDesktopLine1: 'Descrivi la tua esigenza: Dealin ti aiuta a individuare consulenti più adatti',
      boxHeadingDesktopLine2: 'per compliance, protezione dei dati e sicurezza aziendale.',
      ctaLabel: 'Inizia gratuitamente',
    },
    howItWorks: {
      title: 'Un modo più chiaro per trovare il supporto giusto su compliance e sicurezza',
      intro:
        'Dealin ti aiuta a passare da una ricerca generica a una selezione più coerente di consulenti per compliance e sicurezza, in base ai rischi, agli obblighi e alle esigenze reali della tua azienda.',
      items: [
        {
          step: '01',
          title: 'Parti dai bisogni reali della tua azienda',
          desc: 'Non parti da elenchi generici o profili trovati senza contesto: inizi da obblighi, rischi e priorità concrete della tua azienda.',
          icon: FileText,
        },
        {
          step: '02',
          title: 'Professionisti più coerenti con il tuo contesto',
          desc: 'Dealin interpreta la tua richiesta e ti orienta verso consulenti più rilevanti per compliance, protezione dei dati, sicurezza e adeguamento normativo.',
          icon: Users,
        },
        {
          step: '03',
          title: 'Più sicurezza nella valutazione',
          desc: 'Confronti i profili con maggiore chiarezza e puoi scegliere con più consapevolezza da chi partire, in base al supporto di cui hai davvero bisogno.',
          icon: Target,
        },
      ],
    },
    trust: {
      title: 'Informazioni più affidabili per scegliere meglio',
      intro:
        'Con Dealin hai a disposizione informazioni più strutturate e verifiche utili per valutare i professionisti con maggiore consapevolezza, riducendo incertezza e dispersione nella fase di scouting.',
      ctaLabel: 'Trova consulenti',
      items: [
        {
          title: 'Profili verificati',
          desc: 'Le informazioni chiave sui professionisti vengono raccolte e organizzate per offrirti una base più solida da cui partire nella valutazione.',
          icon: Shield,
        },
        {
          title: 'Recensioni verificate',
          desc: 'Le recensioni sono raccolte attraverso un processo di verifica, per aiutarti a leggere l’esperienza dei clienti in modo più affidabile e utile alla scelta.',
          icon: Star,
        },
        {
          title: 'Più chiarezza nel confronto',
          desc: 'Dati, competenze e segnali utili alla valutazione sono presentati in modo più trasparente, per rendere il confronto tra professionisti più semplice e consapevole.',
          icon: Scale,
        },
      ],
    },
  },
};

function storeLandingAIChatHiddenContext(copy: LandingCopy): void {
  writeAIChatHiddenContext({
    source: copy.hiddenContextSource,
    brief_context_type: 'main_landing',
    brief_context_key: copy.landingKey,
    landing_key: copy.landingKey,
    page_title: copy.hero.pageTitle,
    page_subtitle: copy.hero.pageSubtitle,
    ad_keyword: detectGoogleAdsKeyword() || '',
  });
}

function shouldUseBriefFlow(copy: LandingCopy): boolean {
  return copy.landingKey === 'landing-l-com-b';
}

function buildBriefHref(copy: LandingCopy, query = ''): string {
  const params = new URLSearchParams();
  addBriefSeoContextParam(params, copy.landingKey);
  const trimmedQuery = query.trim();
  if (trimmedQuery) {
    params.set('q', trimmedQuery);
  }
  return params.size > 0 ? `/brief?${params.toString()}` : '/brief';
}

const categories = [
  { name: 'Strategia', icon: Target, count: 32, href: '/consulenza/consulenza-strategica' },
  { name: 'Digital Transformation', icon: Zap, count: 52, href: '/consulenza/consulenza-it' },
  { name: 'Finance & Controlling', icon: LineChart, count: 55, href: '/consulenza/consulenza-finanziaria' },
  { name: 'HR & Organizzazione', icon: Users, count: 28, href: '/consulenza/consulenza-hr' },
  { name: 'IT & Cloud', icon: Cpu, count: 68, href: '/consulenza/consulenza-it' },
  { name: 'Marketing & Comunicazione', icon: Megaphone, count: 25, href: '/consulenza/consulenza-marketing' },
  { name: 'Learning & Compliance', icon: Scale, count: 68, href: '/consulenza/certificazioni-compliance' },
  { name: 'Operations & Supply Chain', icon: TrendingUp, count: 30, href: '/consulenza/consulenza-operations' },
];

const ecosystemFeatures = [
  { icon: Brain, title: 'Matching', desc: 'In base alle tue esigenze e caratteristiche selezioniamo per te i migliori consulenti verificati.' },
  { icon: MessageSquare, title: 'Gestione e Comunicazione', desc: 'Utilizza gli strumenti integrati della piattaforma per gestire le tue consulenze in erogazione.' },
  { icon: BarChart3, title: 'Analytics', desc: 'Monitora, attraverso dati aggregati di Dealin, trend e andamenti per prendere la miglior decisione.' },
  { icon: FileText, title: 'AI Assessment', desc: 'Descrivi il tuo progetto alla nostra AI e ricevi un assessment personalizzato con i consulenti più adatti alle tue esigenze.' },
];

const vendorBenefits = [
  'Accesso gratuito alla piattaforma',
  'Riscontro diretto sul mercato con dati e analytics',
  'Sistema di gestione integrato per le tue consulenze',
  'Consigli AI personalizzati',
];

const faqs = [
  {
    question: 'Come funziona la verifica dei profili?',
    answer:
      'Ogni società di consulenza viene verificata tramite firma digitale, Partita IVA e documentazione aziendale. Solo i profili che superano il processo di validazione sono visibili nei risultati di ricerca e nelle shortlist.',
  },
  {
    question: 'Qual è il costo della piattaforma?',
    answer:
      'La registrazione e l\'utilizzo completo di Dealin sono gratuiti sia per le aziende clienti che per le società di consulenza, senza vincoli né carte di credito.',
  },
  {
    question: 'Come funziona l\'AI Assessment?',
    answer:
      'Descrivi il tuo progetto e l\'intelligenza artificiale analizzerà le tue esigenze, genererà un assessment strutturato e ti proporrà una shortlist dei consulenti più adatti con un punteggio di compatibilità.',
  },
  {
    question: 'Come funziona l\'AI matchmaking?',
    answer:
      'Il sistema di intelligenza artificiale analizza i requisiti del progetto, genera un assessment strutturato e confronta le competenze disponibili per restituire una shortlist di consulenti con punteggio di compatibilità.',
  },
  {
    question: 'Come sono gestiti feedback e recensioni?',
    answer:
      'Ogni recensione è collegata a una collaborazione effettiva sulla piattaforma. Il sistema garantisce che solo le aziende con un progetto reale possano lasciare un feedback, assicurando affidabilità e trasparenza.',
  },
];

const defaultTypingPhrases = [
  'Dobbiamo digitalizzare i processi interni della nostra azienda...',
  'Cerchiamo un consulente per implementare un sistema ERP...',
  'Vogliamo espanderci in nuovi mercati europei...',
  'Abbiamo bisogno di ottimizzare la supply chain...',
];

const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-[22px] border border-[#d9e5ff] bg-white/92 px-5 shadow-[0_18px_42px_-34px_rgba(36,87,255,0.28)] sm:px-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-base font-semibold text-[#101828] sm:text-lg">{question}</span>
        {open ? (
          <ChevronUp size={18} className="shrink-0 text-[#2457ff]" />
        ) : (
          <ChevronDown size={18} className="shrink-0 text-[#2457ff]" />
        )}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-48 pb-6 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm leading-7 text-slate-600">{answer}</p>
      </div>
    </div>
  );
};

const EcosystemVisual = () => (
  <div className="relative mx-auto w-full max-w-[620px]">
    <div className="absolute -left-8 top-10 h-40 w-40 rounded-full bg-[#8ae6ff]/22 blur-3xl" />
    <div className="absolute -right-2 top-20 h-44 w-44 rounded-full bg-[#2457ff]/16 blur-3xl" />
    <div className="relative overflow-hidden rounded-[30px] bg-[#071326] p-5 text-white shadow-[0_40px_110px_-58px_rgba(7,19,38,0.82)] ring-1 ring-white/10 sm:p-7">
      <div className="absolute inset-0 opacity-35" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '34px 34px' }} />
      <div className="relative z-10 grid gap-4 sm:grid-cols-2">
        {[
          ['Matching', 'Shortlist pertinenti'],
          ['Gestione', 'Dialogo e lavoro condiviso'],
          ['Analytics', 'Trend e segnali operativi'],
          ['AI Assessment', 'Input chiari e prioritizzati'],
        ].map(([title, sub], index) => (
          <div
            key={title}
            className="min-h-[156px] rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.04)_100%)] p-5 backdrop-blur-sm sm:p-6"
            style={{ animation: `newLandingFloat ${7 + index * 0.4}s ease-in-out infinite`, animationDelay: `${index * 0.2}s` }}
          >
            <div className="h-1.5 w-16 rounded-full bg-gradient-to-r from-[#8ae6ff] via-[#60a5fa] to-[#2457ff]" />
            <div className="mt-4 text-[1.18rem] font-semibold text-white">{title}</div>
            <div className="mt-2 max-w-[18rem] text-sm leading-7 text-slate-300">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const RevealOnScroll = ({
  children,
  className = '',
  delay = 0,
  distance = 28,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  distance?: number;
}) => {
  const shouldDisableReveal =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 767px), (prefers-reduced-motion: reduce)').matches;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(shouldDisableReveal);

  useEffect(() => {
    if (shouldDisableReveal) {
      setIsVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      {
        threshold: 0.16,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldDisableReveal]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate3d(0, 0, 0)' : `translate3d(0, ${distance}px, 0)`,
        transitionProperty: 'opacity, transform',
        transitionDuration: shouldDisableReveal ? '0ms' : '720ms',
        transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
        transitionDelay: shouldDisableReveal ? '0ms' : `${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
};

const HeroSection = ({ copy }: { copy: LandingCopy }) => {
  const navigate = useNavigate();
  const isCompactHeroHeading = copy.landingKey !== 'landing-main';
  const isLongHeroBoxHeading = copy.landingKey === 'landing-l-sec';
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showMobileBriefBar, setShowMobileBriefBar] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const briefCardRef = useRef<HTMLDivElement>(null);
  const { phrases, isPersonalized, isLoading } = useLandingPromptPhrases({
    landingKey: copy.landingKey,
    pageTitle: copy.hero.pageTitle,
    pageSubtitle: copy.hero.pageSubtitle,
    pageCtaHint: copy.hero.pageCtaHint,
    defaultPhrases: defaultTypingPhrases,
  });
  const animationActive = !query && !isFocused && !isLoading;
  const { typedText, showCursor } = useTypingSuggestions({
    active: animationActive,
    phrases,
    holdMs: isPersonalized ? 3600 : 2100,
    initialDelayMs: isPersonalized ? 850 : 600,
    typingMinMs: 38,
    typingVarianceMs: 22,
    deletingMs: 16,
    pauseBetweenPhrasesMs: 380,
  });

  const submitBrief = useCallback((source: string) => {
    PHEvents.ctaClicked('start_assessment', source);
    const trimmed = query.trim();
    if (shouldUseBriefFlow(copy)) {
      navigate(buildBriefHref(copy, trimmed));
      return;
    }
    storeLandingAIChatHiddenContext(copy);
    sessionStorage.setItem(AICHAT_FORCE_NEW_KEY, '1');
    if (trimmed) {
      sessionStorage.setItem(AICHAT_LANDING_MESSAGE_KEY, trimmed);
    }
    navigate('/aichat');
  }, [copy, navigate, query]);

  const handleSearch = (e?: React.FormEvent | React.KeyboardEvent<HTMLTextAreaElement>) => {
    e?.preventDefault();
    submitBrief('new_landing_hero');
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let frameId: number | null = null;

    const updateStickyVisibility = () => {
      const card = briefCardRef.current;
      if (!card) {
        setShowMobileBriefBar(false);
        return;
      }

      const rect = card.getBoundingClientRect();
      setShowMobileBriefBar(rect.bottom <= 32);
    };

    const requestUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updateStickyVisibility);
    };

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#f5f8ff] text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(138,230,255,0.5),_transparent_26%),radial-gradient(circle_at_85%_12%,_rgba(36,87,255,0.16),_transparent_22%),linear-gradient(180deg,_#f5f8ff_0%,_#eff4ff_54%,_#ffffff_100%)]" />
      <div className="absolute inset-x-0 top-0 h-[620px] bg-[linear-gradient(rgba(36,87,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(36,87,255,0.06)_1px,transparent_1px)] bg-[size:46px_46px] opacity-70" />
      <div className="absolute left-[-10%] top-[12%] h-52 w-52 rounded-full bg-[#8ae6ff]/35 blur-3xl" />
      <div className="absolute right-[-8%] top-20 h-72 w-72 rounded-full bg-[#2457ff]/12 blur-3xl" />

      <div className="relative z-10 container mx-auto max-w-7xl px-4 pb-14 pt-10 sm:px-6 sm:pb-20 lg:px-10 lg:pt-14">
        <div className="grid items-center gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:gap-10">
          <div className="max-w-2xl">
            <h1
              className="text-[2.9rem] font-semibold leading-[1.04] tracking-[-0.03em] text-[#081225] sm:text-[4.3rem] lg:text-[5.05rem]"
              style={{ fontFamily: DISPLAY_FONT }}
            >
              {copy.hero.headlinePrefix}
              <span className="text-[#2457ff]">{copy.hero.headlineHighlight}</span>
              {copy.hero.headlineConnector}
              {copy.hero.headlineSecondHighlight ? (
                <span className="text-[#2457ff]">{copy.hero.headlineSecondHighlight}</span>
              ) : null}
              {copy.hero.headlineSuffix}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
              {copy.hero.intro}
            </p>
          </div>

          <div className="relative hidden min-h-[420px] items-center justify-center md:flex lg:min-h-[560px]">
            <div className="w-full max-w-[420px] origin-top scale-[0.76] sm:scale-[0.9] lg:scale-100">
              <Suspense fallback={<div className="h-[540px] w-full" />}>
                <AnimatedConsultantCard />
              </Suspense>
            </div>
          </div>
        </div>

        <form onSubmit={handleSearch} className="mt-7 sm:mt-12">
          <div className="new-landing-ai-stage relative mx-auto max-w-[1160px] overflow-visible">
            <div className="new-landing-ai-mesh-top pointer-events-none absolute -right-14 top-0 h-[280px] w-[420px] opacity-45">
                  <svg
                    viewBox="0 0 900 700"
                    className="h-full w-full"
                    preserveAspectRatio="xMaxYMin meet"
                    style={{ animation: 'newLandingMeshDrift 15s ease-in-out infinite' }}
                  >
                    <circle cx="200" cy="100" r="5" fill="#3b82f6" style={{ animation: 'newLandingMeshPulse 3.8s ease-in-out infinite' }} />
                    <circle cx="450" cy="70" r="5" fill="#3b82f6" style={{ animation: 'newLandingMeshPulse 4.4s ease-in-out infinite' }} />
                    <circle cx="700" cy="130" r="5" fill="#3b82f6" style={{ animation: 'newLandingMeshPulse 4s ease-in-out infinite' }} />
                    <circle cx="330" cy="250" r="5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 4.6s ease-in-out infinite' }} />
                    <circle cx="580" cy="270" r="5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 3.7s ease-in-out infinite' }} />
                    <circle cx="800" cy="300" r="4.5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 4.3s ease-in-out infinite' }} />
                    <circle cx="240" cy="420" r="4.5" fill="#93c5fd" style={{ animation: 'newLandingMeshPulse 4.1s ease-in-out infinite' }} />
                    <circle cx="500" cy="450" r="5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 4.8s ease-in-out infinite' }} />
                    <circle cx="720" cy="480" r="4.5" fill="#93c5fd" style={{ animation: 'newLandingMeshPulse 3.9s ease-in-out infinite' }} />
                    <line x1="200" y1="100" x2="450" y2="70" stroke="#3b82f6" strokeWidth="1.8" />
                    <line x1="450" y1="70" x2="700" y2="130" stroke="#3b82f6" strokeWidth="1.8" />
                    <line x1="200" y1="100" x2="330" y2="250" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="450" y1="70" x2="330" y2="250" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="450" y1="70" x2="580" y2="270" stroke="#3b82f6" strokeWidth="1.5" />
                    <line x1="700" y1="130" x2="580" y2="270" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="700" y1="130" x2="800" y2="300" stroke="#3b82f6" strokeWidth="1.5" />
                    <line x1="330" y1="250" x2="580" y2="270" stroke="#60a5fa" strokeWidth="1.5" />
                    <line x1="580" y1="270" x2="800" y2="300" stroke="#60a5fa" strokeWidth="1.3" />
                    <line x1="330" y1="250" x2="240" y2="420" stroke="#93c5fd" strokeWidth="1.3" />
                    <line x1="330" y1="250" x2="500" y2="450" stroke="#60a5fa" strokeWidth="1.3" />
                    <line x1="580" y1="270" x2="500" y2="450" stroke="#93c5fd" strokeWidth="1.3" />
                    <line x1="580" y1="270" x2="720" y2="480" stroke="#60a5fa" strokeWidth="1.3" />
                    <line x1="800" y1="300" x2="720" y2="480" stroke="#93c5fd" strokeWidth="1.2" />
                    <line x1="240" y1="420" x2="500" y2="450" stroke="#93c5fd" strokeWidth="1.2" />
                    <line x1="500" y1="450" x2="720" y2="480" stroke="#bfdbfe" strokeWidth="1.2" />
                  </svg>
            </div>

            <div className="new-landing-ai-mesh-bottom pointer-events-none absolute -bottom-12 -left-16 h-[240px] w-[300px] opacity-35">
                  <svg
                    viewBox="0 0 600 500"
                    className="h-full w-full"
                    preserveAspectRatio="xMinYMax meet"
                    style={{ animation: 'newLandingMeshDrift 18s ease-in-out infinite reverse' }}
                  >
                    <circle cx="100" cy="120" r="5" fill="#3b82f6" style={{ animation: 'newLandingMeshPulse 4.3s ease-in-out infinite' }} />
                    <circle cx="300" cy="80" r="5" fill="#3b82f6" style={{ animation: 'newLandingMeshPulse 3.8s ease-in-out infinite' }} />
                    <circle cx="500" cy="150" r="4.5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 4.1s ease-in-out infinite' }} />
                    <circle cx="180" cy="280" r="5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 4.7s ease-in-out infinite' }} />
                    <circle cx="400" cy="300" r="5" fill="#60a5fa" style={{ animation: 'newLandingMeshPulse 3.9s ease-in-out infinite' }} />
                    <circle cx="120" cy="420" r="4" fill="#93c5fd" style={{ animation: 'newLandingMeshPulse 4.5s ease-in-out infinite' }} />
                    <circle cx="320" cy="440" r="4" fill="#93c5fd" style={{ animation: 'newLandingMeshPulse 4s ease-in-out infinite' }} />
                    <line x1="100" y1="120" x2="300" y2="80" stroke="#3b82f6" strokeWidth="1.5" />
                    <line x1="300" y1="80" x2="500" y2="150" stroke="#3b82f6" strokeWidth="1.5" />
                    <line x1="100" y1="120" x2="180" y2="280" stroke="#60a5fa" strokeWidth="1.3" />
                    <line x1="300" y1="80" x2="180" y2="280" stroke="#60a5fa" strokeWidth="1.2" />
                    <line x1="300" y1="80" x2="400" y2="300" stroke="#60a5fa" strokeWidth="1.3" />
                    <line x1="500" y1="150" x2="400" y2="300" stroke="#60a5fa" strokeWidth="1.2" />
                    <line x1="180" y1="280" x2="400" y2="300" stroke="#93c5fd" strokeWidth="1.2" />
                    <line x1="180" y1="280" x2="120" y2="420" stroke="#93c5fd" strokeWidth="1.1" />
                    <line x1="180" y1="280" x2="320" y2="440" stroke="#93c5fd" strokeWidth="1.1" />
                    <line x1="400" y1="300" x2="320" y2="440" stroke="#bfdbfe" strokeWidth="1" />
                    <line x1="120" y1="420" x2="320" y2="440" stroke="#bfdbfe" strokeWidth="1" />
                  </svg>
            </div>

            <div className="new-landing-ai-content relative px-0 py-3 sm:px-8 sm:py-6">
              <div
                className={`mx-auto max-w-none px-0 text-center sm:max-w-4xl sm:px-0 ${
                  isLongHeroBoxHeading ? 'lg:max-w-[1120px]' : 'lg:max-w-[860px]'
                }`}
              >
                <h3
                  className={`text-[1.06rem] font-semibold tracking-[-0.03em] text-[#101828] ${
                    isLongHeroBoxHeading
                      ? 'leading-[1.16] sm:text-[1.45rem] sm:leading-[1.18] lg:text-[1.72rem]'
                      : isCompactHeroHeading
                      ? 'leading-[1.12] sm:text-[2.05rem] sm:leading-[1.18] lg:text-[2.4rem]'
                      : 'leading-[1.08] sm:text-[2.6rem] sm:leading-[1.14] lg:text-[3.05rem]'
                  }`}
                  style={{ fontFamily: DISPLAY_FONT }}
                >
                  <span className="sm:hidden">
                    <span
                      className="inline-block whitespace-normal bg-[linear-gradient(90deg,#18325c_0%,#22478a_42%,#2b5fc0_68%,#3d72ff_100%)] bg-clip-text px-[0.01em] pb-[0.14em] text-[clamp(1.16rem,5.18vw,1.56rem)] font-semibold leading-[1.08] tracking-[-0.03em] text-transparent"
                      style={{ backgroundSize: '160% 100%', animation: 'aiGradientShift 4.8s ease-in-out infinite alternate' }}
                    >
                      {copy.hero.boxHeadingMobile}
                    </span>
                  </span>
                  {isCompactHeroHeading ? (
                    <>
                      <span className={`hidden sm:block ${isLongHeroBoxHeading ? 'lg:whitespace-nowrap' : 'whitespace-nowrap'}`}>
                        {copy.hero.boxHeadingDesktopLine1}
                      </span>
                      <span className={`hidden sm:block ${isLongHeroBoxHeading ? 'lg:whitespace-nowrap' : 'whitespace-nowrap'}`}>
                        {copy.hero.boxHeadingDesktopLine2}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">{copy.hero.boxHeadingDesktopLine1}</span>
                      <span className="hidden sm:inline">
                        <br className="hidden sm:block" />
                        {copy.hero.boxHeadingDesktopLine2}
                      </span>
                    </>
                  )}
                </h3>
              </div>

              <div className="mx-auto mt-5 max-w-none sm:mt-10 sm:max-w-4xl lg:max-w-[1020px]">
                <div className="mb-4 hidden flex-col items-center gap-2 px-2 text-center md:mb-3 md:flex md:flex-row md:flex-wrap md:items-center md:gap-2 md:px-1 md:text-left">
                  <div className="flex flex-col items-center gap-1.5 md:flex-row md:items-center md:gap-2">
                    <Sparkles size={14} className="hidden shrink-0 text-[#2563EB] md:block" />
                    <span className="text-[1rem] font-semibold leading-tight text-slate-900 md:text-sm md:font-medium md:text-slate-700">
                      Analisi AI gratuita
                    </span>
                  </div>
                  <span className="hidden text-slate-300 md:inline">·</span>
                  <span className="max-w-[19rem] text-sm leading-6 text-slate-600 md:max-w-none md:text-sm md:leading-normal md:text-slate-400">
                    Descrivi la tua richiesta e ottieni un primo assessment strutturato in pochi minuti
                  </span>
                </div>
                <div
                  ref={briefCardRef}
                  className="relative overflow-hidden rounded-2xl p-[2px]"
                  style={{
                    background: isFocused
                      ? 'linear-gradient(135deg, #bfdbfe, #2563EB, #93c5fd, #1d4ed8, #bfdbfe)'
                      : 'linear-gradient(135deg, #e2e8f0, #cbd5e1, #dde3ed, #c8d3e3, #e2e8f0)',
                    boxShadow: isFocused
                      ? '0 0 0 4px rgba(37,99,235,0.10), 0 20px 40px -8px rgba(37,99,235,0.18)'
                      : '0 10px 40px -8px rgba(148,163,184,0.22)',
                    transition: 'background 0.4s ease, box-shadow 0.3s ease',
                  }}
                  onClick={() => textareaRef.current?.focus()}
                >
                  <div
                    className="absolute left-1/2 top-1/2 z-[1] h-[500%] w-[500%] -translate-x-1/2 -translate-y-1/2"
                    style={{
                      background:
                        'conic-gradient(from 0deg, transparent 0deg, #dbeafe 18deg, #93c5fd 48deg, #60a5fa 90deg, #2563EB 155deg, #60a5fa 220deg, #93c5fd 278deg, #dbeafe 314deg, transparent 332deg, transparent 360deg)',
                      animation: 'spin-smooth 3.5s linear infinite',
                      opacity: isFocused ? 1 : 0.75,
                      transition: 'opacity 0.4s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  <div className="relative z-[2] overflow-hidden rounded-[14px] bg-white">
                    <div className="relative">
                      <textarea
                        ref={textareaRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={handleFocus}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSearch(e);
                          }
                        }}
                        placeholder={isFocused ? 'Descrivi la tua esigenza aziendale...' : ''}
                        className="w-full resize-none bg-transparent px-6 pb-3 pt-5 text-base leading-relaxed text-slate-800 outline-none placeholder:text-slate-400 sm:px-7 sm:pt-6 sm:text-lg"
                        rows={5}
                      />

                      {animationActive && (
                        <div className="pointer-events-none absolute inset-0 px-6 pt-5 sm:px-7 sm:pt-6">
                          <span className="text-base leading-relaxed text-slate-400 sm:text-lg">{typedText}</span>
                          <span
                            className={`ml-[1px] inline-block h-[1.15em] w-[2px] rounded-full bg-[#2563EB] align-middle transition-opacity duration-100 ${showCursor ? 'opacity-70' : 'opacity-0'}`}
                          />
                        </div>
                      )}
                    </div>

                    <div className="px-4 sm:px-8">
                      <div
                        style={{
                          height: '1px',
                          borderRadius: '9999px',
                          background:
                            'linear-gradient(90deg, transparent 0%, #cbd5e1 8%, #94a3b8 30%, #94a3b8 70%, #cbd5e1 92%, transparent 100%)',
                          opacity: 0.55,
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-7">
                      <div className="flex min-w-0 items-center gap-2 text-[11px] text-slate-400 sm:gap-3 sm:text-xs">
                        <Shield size={13} className="text-green-500/70" />
                        <span className="truncate">Dati riservati</span>
                      </div>
                      <button
                        type="submit"
                        className="ml-auto flex shrink-0 items-center gap-2 rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white shadow-md shadow-[#2563EB]/25 transition-all hover:bg-[#1d4ed8] hover:shadow-lg hover:shadow-[#2563EB]/30 active:scale-[0.97] sm:gap-2.5 sm:px-8 sm:py-3.5 sm:text-[15px]"
                      >
                        {copy.hero.ctaLabel}
                        <ArrowRight size={17} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <RevealOnScroll className="mx-auto mt-5 max-w-none px-1 sm:mt-6 sm:max-w-4xl sm:px-0 lg:max-w-[1020px]" delay={90} distance={18}>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Check size={15} className="text-[#2563EB]" />
                    Diagnosi del problema
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check size={15} className="text-[#2563EB]" />
                    Stima obiettivi e tempi
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check size={15} className="text-[#2563EB]" />
                    Piano d'azione
                  </span>
                  <span className="flex items-center gap-1.5 sm:hidden">
                    <Check size={15} className="text-[#2563EB]" />
                    Completamente gratuito
                  </span>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </form>
      </div>

      <FloatingBriefPrompt
        visible={showMobileBriefBar}
        query={query}
        onQueryChange={setQuery}
        onSubmit={() => submitBrief('new_landing_mobile_sticky')}
      />
    </section>
  );
};

const NewLandingPage = ({
  variant = 'main',
  canonicalOverride,
}: {
  variant?: LandingVariant;
  canonicalOverride?: string;
}) => {
  const copy = landingCopy[variant];

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f8ff] text-slate-900">
      <SEOHead
        title={copy.seo.title}
        description={copy.seo.description}
        canonical={canonicalOverride || copy.seo.canonical}
        noindex={copy.seo.noindex}
      />

      <style>{`
        @keyframes spin-smooth {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes newLandingFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes newLandingMeshDrift {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50% { transform: translate3d(-10px, 4px, 0); }
        }

        @keyframes newLandingMeshPulse {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.18); }
        }

        @keyframes newLandingStickySparkle {
          0%, 100% { transform: translateY(0) scale(0.9); opacity: 0.6; }
          40% { transform: translateY(-1px) scale(1.08); opacity: 1; }
          65% { transform: translateY(-2px) scale(1.18); opacity: 0.9; }
        }

        @keyframes aiGradientShift {
          0% { background-position: 24% 50%; }
          100% { background-position: 38% 50%; }
        }

        @media (max-width: 639px) {
          .new-landing-ai-content {
            padding-top: 0.5rem !important;
            padding-bottom: 0 !important;
          }

          .new-landing-ai-mesh-top {
            right: -3.25rem !important;
            top: -1.75rem !important;
            width: 16rem !important;
            height: 11rem !important;
            opacity: 0.38 !important;
          }

          .new-landing-ai-mesh-bottom {
            left: -3.5rem !important;
            bottom: -2.5rem !important;
            width: 13rem !important;
            height: 10rem !important;
            opacity: 0.28 !important;
          }
        }
      `}</style>

      <HeroSection copy={copy} />

      <section className="relative py-14 sm:py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6">
          <RevealOnScroll className="max-w-3xl">
            <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-[#081225] sm:text-[3.45rem]" style={{ fontFamily: DISPLAY_FONT }}>
              {copy.howItWorks.title}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg">
              {copy.howItWorks.intro}
            </p>
          </RevealOnScroll>

          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {copy.howItWorks.items.map((item, index) => (
              <RevealOnScroll key={item.step} className="h-full" delay={index * 90}>
                <div className="relative h-full overflow-hidden rounded-[24px] border border-[#d9e5ff] bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(246,250,255,0.98)_100%)] p-5 shadow-[0_26px_54px_-42px_rgba(36,87,255,0.24)] sm:p-6">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#93c5fd] to-transparent" />
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#081225] text-[#8ae6ff]">
                      <item.icon size={22} strokeWidth={1.7} />
                    </div>
                  </div>
                  <div className="mt-7">
                    <h3 className="text-[1.45rem] font-semibold leading-tight text-[#081225]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                      {item.desc.includes('consulenti verificati') ? (
                        <>
                          {item.desc.split('consulenti verificati')[0]}
                          <span className="font-semibold text-[#2457ff]">consulenti verificati</span>
                          {item.desc.split('consulenti verificati')[1]}
                        </>
                      ) : (
                        item.desc
                      )}
                    </p>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#ffffff_0%,#f6f9ff_100%)] py-14 sm:py-16 md:py-20">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9e5ff] to-transparent" />
        <div className="absolute left-[8%] top-12 h-40 w-40 rounded-full bg-[#8ae6ff]/18 blur-3xl" />
        <div className="container mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-10 xl:grid-cols-[0.84fr_1.16fr] xl:gap-12">
            <RevealOnScroll className="xl:pr-4">
              <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-[#081225] sm:text-[3.35rem]" style={{ fontFamily: DISPLAY_FONT }}>
                {copy.trust.title}
              </h2>
              <p className="mt-4 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
                {copy.trust.intro}
              </p>
              <div className="mt-7">
                <Button className="rounded-full bg-[#2457ff] px-6 text-white hover:bg-[#1e49d9]" asChild>
                  <Link to="/register" onClick={() => PHEvents.ctaClicked('register', 'new_landing')}>
                    {copy.trust.ctaLabel}
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>
            </RevealOnScroll>

            <div className="grid gap-4 sm:grid-cols-2">
              {copy.trust.items.map((item, index) => (
                <RevealOnScroll
                  key={item.title}
                  className={index === 0 ? 'sm:col-span-2' : ''}
                  delay={index * 90}
                >
                  <div
                    className={`h-full border p-5 sm:p-6 ${
                      index === 0
                        ? 'rounded-[24px] border-[#cddcff] bg-[linear-gradient(135deg,#edf4ff_0%,#f8fbff_100%)]'
                        : 'rounded-[20px] border-[#d9e5ff] bg-white/88'
                    }`}
                  >
                    <div>
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] bg-white text-[#2457ff] ring-1 ring-[#d9e5ff]">
                            <item.icon size={18} strokeWidth={1.8} />
                          </div>
                          <div className="h-px flex-1 bg-gradient-to-r from-[#d9e5ff] to-transparent" />
                        </div>
                      </div>
                      <div>
                        <h3 className="mt-5 text-xl font-semibold text-[#081225] sm:mt-0">{item.title}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-600">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#081225] py-14 sm:py-16 md:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,_rgba(138,230,255,0.16),_transparent_24%),radial-gradient(circle_at_84%_14%,_rgba(36,87,255,0.24),_transparent_22%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(138,230,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(138,230,255,0.05)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />
        <div className="relative z-10 container mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-start gap-12 lg:grid-cols-[0.98fr_1.02fr] lg:gap-14">
            <RevealOnScroll>
              <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-white sm:text-[3.35rem]" style={{ fontFamily: DISPLAY_FONT }}>
                Una piattaforma unica per l'intero ciclo di vita della consulenza
              </h2>
              <p className="mt-4 max-w-xl text-base leading-8 text-slate-300 sm:text-lg">
                Gestisci l'intero ciclo di vita dei tuoi progetti, dall'ingaggio alla comunicazione e dal
                monitoraggio fino alla conclusione; il tutto supportato da strumenti dedicati.
              </p>

              <div className="mt-8 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                {ecosystemFeatures.map((feature) => (
                  <div key={feature.title} className="border-b border-white/10 pb-5">
                    <div className="flex items-start gap-4">
                      <div className="rounded-[16px] border border-white/10 bg-white/5 p-3 text-[#8ae6ff]">
                        <feature.icon size={18} strokeWidth={1.7} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-white">{feature.title}</h3>
                        <p className="mt-1 text-sm leading-7 text-slate-300">{feature.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7">
                <Button variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" asChild>
                  <Link to="/home">Esplora la piattaforma</Link>
                </Button>
              </div>
            </RevealOnScroll>

            <RevealOnScroll className="hidden md:block" delay={130}>
              <EcosystemVisual />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      <section className="relative py-14 sm:py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-start gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10">
            <RevealOnScroll className="order-2 lg:order-1">
              <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-[#081225] sm:text-[3.35rem]" style={{ fontFamily: DISPLAY_FONT }}>
                Sei un' Azienda di Consulenza?
              </h2>
              <p className="mt-4 text-lg font-medium leading-8 text-[#2457ff]">
                Iscriviti adesso per cominciare ad esplorare Dealin e strutturare la tua offerta
              </p>
              <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
                Come piattaforma incentrata sulla consulenza, Dealin offre alle aziende di consulenza la possibilità
                di strutturare la propria offerta, rispondere ad esigenze reali, monitorare il proprio andamento e molto altro.
              </p>
              <div className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                {vendorBenefits.map((item) => (
                  <div key={item} className="flex items-start gap-3 border-b border-[#d9e5ff] pb-4">
                    <div className="mt-0.5 rounded-full bg-[#eef4ff] p-1.5 text-[#2457ff] ring-1 ring-[#d9e5ff]">
                      <Check size={12} />
                    </div>
                    <span className="text-sm leading-7 text-slate-700 sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-7">
                <Button className="rounded-full bg-[#2457ff] px-6 text-white hover:bg-[#1e49d9]" asChild>
                  <Link to="/register" onClick={() => PHEvents.ctaClicked('register', 'new_landing')}>
                    Registra la tua azienda
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>
            </RevealOnScroll>

            <RevealOnScroll className="relative order-1 lg:order-2" delay={130}>
              <div className="rounded-[28px] border border-[#d9e5ff] bg-[linear-gradient(180deg,#ffffff_0%,#f6f9ff_100%)] p-4 shadow-[0_30px_70px_-54px_rgba(36,87,255,0.26)] sm:p-5">
                <div className="rounded-[24px] bg-[linear-gradient(180deg,_#081225,_#14316b)] p-5 text-white sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#8ae6ff] text-lg font-bold text-[#081225]">
                      AC
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-base font-bold">Alpha Consulting</div>
                      <div className="text-sm text-slate-300">Strategia & Innovazione</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-sm">
                      <Star size={14} className="fill-[#8ae6ff] text-[#8ae6ff]" />
                      <span>4.9</span>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-3 gap-3">
                    {[{ label: 'Progetti', value: '47' }, { label: 'Esperienza', value: '12 anni' }, { label: 'Team', value: '25+' }].map((stat) => (
                      <div key={stat.label} className="rounded-[16px] bg-white/8 p-3 text-center">
                        <div className="text-lg font-semibold text-white">{stat.value}</div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {['SAP', 'Microsoft', 'Cloud AWS', 'Data Analytics'].map((tag) => (
                      <span key={tag} className="rounded-full bg-white/10 px-3 py-1 text-xs text-[#d8ecff] ring-1 ring-white/10">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center gap-3 rounded-[16px] bg-[#8ae6ff] px-4 py-3 text-[#081225]">
                    <Shield size={15} />
                    <span className="text-sm font-medium">Profilo verificato con firma digitale</span>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      <section id="categories-section" className="relative overflow-hidden py-14 sm:py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6">
          <RevealOnScroll className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-[#081225] sm:text-[3.35rem]" style={{ fontFamily: DISPLAY_FONT }}>
                Esplora per area di competenza
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg">
                Consulenti e società di consulenza specializzati per ogni esigenza aziendale.
              </p>
            </div>
              <Link
                to={shouldUseBriefFlow(copy) ? buildBriefHref(copy) : '/aichat'}
                onClick={() => {
                  if (!shouldUseBriefFlow(copy)) {
                    storeLandingAIChatHiddenContext(copy);
                    sessionStorage.setItem(AICHAT_FORCE_NEW_KEY, '1');
                  }
                  PHEvents.ctaClicked('start_assessment', 'new_landing_categories');
                }}
                className="inline-flex items-center gap-2 self-start rounded-full bg-[#2457ff] px-7 py-3.5 text-base font-semibold text-white transition hover:bg-[#1e49d9] lg:self-auto"
              >
                <Sparkles size={18} />
                Inizia
                <ArrowRight size={16} />
              </Link>
          </RevealOnScroll>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {categories.map((category, index) => (
              <RevealOnScroll key={category.name} className="h-full" delay={index * 70}>
                <Link
                  to={category.href}
                  onClick={() => PHEvents.ctaClicked('open_area', `new_landing_categories_${category.name.toLowerCase().replace(/\s+/g, '_')}`)}
                  className="group block h-full rounded-[22px] border border-[#d9e5ff] bg-white/92 p-5 text-[#081225] shadow-[0_18px_40px_-34px_rgba(36,87,255,0.18)] transition duration-300 hover:-translate-y-1 hover:border-[#2457ff]/25 hover:shadow-[0_24px_50px_-38px_rgba(36,87,255,0.28)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#2457ff]">
                      <category.icon size={22} strokeWidth={1.7} />
                    </div>
                    <span className="rounded-full border border-[#d9e5ff] px-3 py-1 text-xs text-slate-500">
                      {category.count}+ esperti
                    </span>
                  </div>
                  <div className="mt-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#2457ff]">
                    Area
                  </div>
                  <div className="mt-1 text-lg font-semibold leading-snug">{category.name}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#2457ff]">
                    Vai all'area
                    <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[linear-gradient(180deg,#f4f8ff_0%,#eef4ff_100%)] py-14 sm:py-16 md:py-20">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6">
          <RevealOnScroll className="text-center">
            <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-[#081225] sm:text-[3.35rem]" style={{ fontFamily: DISPLAY_FONT }}>
              Domande frequenti
            </h2>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">
              Le informazioni principali per conoscere la piattaforma.
            </p>
          </RevealOnScroll>
          <div className="mt-10 space-y-3">
            {faqs.map((faq, index) => (
              <RevealOnScroll key={faq.question} delay={index * 70} distance={18}>
                <FaqItem question={faq.question} answer={faq.answer} />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#081225] py-16 sm:py-20 md:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,_rgba(138,230,255,0.2),_transparent_24%),radial-gradient(circle_at_80%_20%,_rgba(36,87,255,0.24),_transparent_26%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(138,230,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(138,230,255,0.05)_1px,transparent_1px)] bg-[size:44px_44px] opacity-15" />
        <RevealOnScroll className="relative z-10 container mx-auto max-w-5xl px-4 text-center sm:px-6">
          <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-white sm:text-5xl lg:text-6xl" style={{ fontFamily: DISPLAY_FONT }}>
            Entra nell'ecosistema Dealin
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
            Accedi alla piattaforma che rende l'incontro tra domanda e offerta di consulenza più semplice,
            trasparente e misurabile.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button className="h-auto w-full rounded-full bg-[#2457ff] px-8 py-4 text-base text-white hover:bg-[#1e49d9] sm:w-auto" asChild>
              <Link to="/register" onClick={() => PHEvents.ctaClicked('register', 'new_landing')}>
                Registrati gratis
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
            <Button variant="outline" className="h-auto w-full rounded-full border-white/15 bg-white/5 px-8 py-4 text-base text-white hover:bg-white/10 sm:w-auto" asChild>
              <Link to="/home">Esplora la piattaforma</Link>
            </Button>
          </div>

          <p className="mt-6 text-sm text-slate-400">
            Accesso gratuito · Nessuna carta richiesta · Operativo in pochi minuti
          </p>

          <div className="mx-auto mt-12 max-w-3xl rounded-[24px] border border-white/10 bg-white px-6 py-8 text-[#081225] shadow-[0_30px_90px_-54px_rgba(15,23,42,0.6)] sm:px-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#eef4ff] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#2457ff] ring-1 ring-[#d9e5ff]">
              <Sparkles size={13} />
              Analisi AI gratuita
            </div>
            <h3 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-[#081225] sm:text-4xl" style={{ fontFamily: DISPLAY_FONT }}>
              Non sai da dove partire?
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
              Descrivi il tuo progetto in poche righe e la nostra AI genererà un assessment professionale con
              i consulenti più adatti — in meno di 3 minuti.
            </p>
            <div className="mt-7">
              <Button className="h-auto rounded-full bg-[#2457ff] px-8 py-4 text-base text-white hover:bg-[#1e49d9]" asChild>
                <Link
                  to={shouldUseBriefFlow(copy) ? buildBriefHref(copy) : '/aichat'}
                  onClick={() => {
                    if (!shouldUseBriefFlow(copy)) {
                      storeLandingAIChatHiddenContext(copy);
                      sessionStorage.setItem(AICHAT_FORCE_NEW_KEY, '1');
                    }
                    PHEvents.ctaClicked('start_assessment', 'new_landing_final_cta');
                  }}
                >
                  Prova l'analisi gratuita
                  <Sparkles className="ml-2" size={18} />
                </Link>
              </Button>
            </div>
          </div>
        </RevealOnScroll>
      </section>
    </div>
  );
};

export default NewLandingPage;


